import config from '../config';
import React, { useState, useEffect } from "react";
import "../style/createAccount.css";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { isUUID } from "validator";
import { useAuth } from "../context/authContext";
import Button from "../components/Button";
import { UserPen } from "lucide-react";

const MODULE_LABELS = {
  cleftLip: "Cleft Lip & Palate",
  preNatal: "Antenatal",
  clearance: "Clearance",
  plannedEvent: "Planned Event",
  discharge: "Discharge",
  providerNote: "Provider Note",
  dentalVisit: "Dental Visit",
  cervicalCancer: "Cervical Cancer",
  woundCare: "Wound Care",
  //activeLists: "Active Lists",
};

const AddAccount = () => {
  const { authData } = useAuth();
  const location = useLocation();
  const { orgId, orgName, editMode, accountId } = location.state;
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("success");
  const todayDate = new Date().toISOString().split("T")[0];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialFormData, setInitialFormData] = useState(null);
  const [orgVastOnly, setOrgVastOnly] = useState(false);

  const [title, setTitle] = useState("Create Account");

  useEffect(() => {
    if (editMode) {
      setTitle("Edit Account");
    }
  }, [editMode]);

  const [formData, setFormData] = useState({
    name: "",
    joinedDate: "",
    notes: "",
    active: false,
    modules: {
      cleftLip: false,
      generalSurgery: false,
      inPatient: false,
      laborDelivery: false,
      orthopedic: false,
      pediatric: false,
      preNatal: false,
      primaryCare: false,
      physiotherapy: false,
      wheelchair: false,
      clearance: false,
      plannedEvent: false,
      discharge: false,
      providerNote: false,
      dentalVisit: false,
      cervicalCancer: false,
      woundCare: false,
     // activeLists: false,
    },
  });

  useEffect(() => {
    if (!editMode) {
      setFormData((prev) => ({
        ...prev,
        joinedDate: todayDate,
      }));
    }
  }, [editMode, todayDate]);

  // Fetch organization data to check vastOnly flag
  useEffect(() => {
    const fetchOrgData = async () => {
      if (!orgId || !isUUID(orgId)) {
        return;
      }

      try {
        const response = await fetch(
          `${config.BASE_URL}/organizations/${orgId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const orgData = await response.json();
          setOrgVastOnly(orgData.vastOnly || false);
        }
      } catch (error) {
        console.error("Error fetching organization data:", error);
      }
    };

    fetchOrgData();
  }, [orgId, authData.accessToken]);

  // Store initial form data when editing
  useEffect(() => {
    if (editMode && !loading && !initialFormData) {
      setInitialFormData(formData);
    }
  }, [editMode, loading, formData, initialFormData]);

  const hasFormChanges = () => {
    if (!editMode || !initialFormData) return true;
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  };

  const isFormValid = () => {
    if (editMode) return hasFormChanges();
    return (
      formData.name.trim() !== "" &&
      Object.values(formData.modules).some(module => module === true)
    );
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleModuleChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      modules: {
        ...prevData.modules,
        [name]: checked,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid() || isSubmitting) return;

    setIsSubmitting(true);

    const selectedModules = Object.keys(formData.modules).filter(
      (module) => formData.modules[module] === true
    );

    if (selectedModules.length === 0) {
      alert("Please select at least one module.");
      setIsSubmitting(false);
      return;
    }

    const data = {
      name: formData.name,
      joinedDate: formData.joinedDate || null,
      notes: formData.notes,
      active: formData.active,
      modules: selectedModules,
    };

    try {
      const payload = {
        ...data,
        OrganizationId: orgId,
      };

      const method = editMode ? "PUT" : "POST";
      const url = editMode
        ? `${config.BASE_URL}/accounts/${accountId}`
        : `${config.BASE_URL}/accounts`;

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${authData.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        setPopupMessage(
          editMode ? "Error updating account!" : "Error saving account!"
        );
        setPopupType("error");
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 4000);
        setIsSubmitting(false);

        throw new Error(
          editMode
            ? `Failed to update account: ${response.statusText}`
            : `Failed to create account: ${response.statusText}`
        );
      }

      await response.json();

      setPopupMessage(
        editMode
          ? "Account updated successfully!"
          : "Account saved successfully!"
      );
      setPopupType("success");
      setShowPopup(true);

      setTimeout(() => {
        setShowPopup(false);
        navigate(`/organization/${orgId}`, {
          state: { orgId, orgName, accountUpdated: true },
        });
      }, 4000);
    } catch (error) {
      console.error("Error saving account:", error);
      setPopupMessage(
        editMode ? "Error updating account!" : "Error saving account!"
      );
      setPopupType("error");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 4000);
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchAccData = async () => {
      if (!editMode || !isUUID(accountId)) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${config.BASE_URL}/accounts/${accountId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch account data");
        }

        const accData = await response.json();
        console.log("Received account data:", accData);

        const modulesState = Object.keys(formData.modules).reduce(
          (acc, module) => {
            acc[module] = accData.modules?.includes(module) || false;
            return acc;
          },
          {}
        );

        let formattedDate = "";
        if (accData.joinedDate) {
          try {
            const date = new Date(accData.joinedDate);
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, "0");
              const day = String(date.getDate()).padStart(2, "0");
              formattedDate = `${year}-${month}-${day}`;
              console.log("Formatted date:", formattedDate);
            } else {
              console.error("Invalid date:", accData.joinedDate);
            }
          } catch (error) {
            console.error("Error parsing date:", error);
          }
        }

        setFormData({
          name: accData.name || "",
          joinedDate: formattedDate,
          notes: accData.notes || "",
          active: accData.active || false,
          modules: modulesState,
        });
      } catch (error) {
        console.error("Error fetching account data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAccData();
  }, [editMode, accountId, authData.accessToken]);

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <div className="addAccount-page">
      {showPopup && <div className={`popup-${popupType}`}>{popupMessage}</div>}

      <div className="addAccount-content-wrapper">
        <div className="addAccount-header">
          <Button
            className="addAccount-backButton"
            onClick={handleBackClick}
            text="← Back"
            color="#006d7d"
          />
          <h1>{title}</h1>
        </div>
        <div className="addAccount-formContainer">
          <form onSubmit={handleSubmit}>
            <div className="addAccount-formContent">
              <div className="addAccount-formLeft">
                <div className="addAccount-formGroup">
                  <label htmlFor="name">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="addAccount-formGroup">
                  <label htmlFor="joinedDate">Entered Date</label>
                  <input
                    type="date"
                    id="joinedDate"
                    name="joinedDate"
                    value={formData.joinedDate}
                    onChange={handleInputChange}
                    {...(!editMode && { min: todayDate })}
                  />
                </div>
                <div className="addAccount-formGroup">
                  <label htmlFor="notes">Notes</label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="addAccount-formGroup">
                  <label htmlFor="active">Active</label>
                  <div className="addAccount-checkboxWrapper">
                    <input
                      type="checkbox"
                      id="active"
                      name="active"
                      checked={formData.active}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
              <div className="addAccount-formRight">
                <div className="addAccount-modulesSection">
                  <h2>Modules</h2>
                  <div className="addAccount-modulesList">
                    {Object.entries(formData.modules)
                      .filter(([key, value]) => {


                        const vastOnlyModules = ['clearance', 'plannedEvent', 'discharge', 'providerNote', 'activeLists'];
                        if (vastOnlyModules.includes(key)) {
                          return orgVastOnly || value === true;
                        }
                        return true;
                      })
                      .map(([key, value]) => (
                        <label key={key}>
                          <input
                            type="checkbox"
                            name={key}
                            checked={value}
                            onChange={handleModuleChange}
                          />
                          {MODULE_LABELS[key]
                            ?? key
                              .replace(/([A-Z])/g, " $1")
                              .replace(/^./, (str) => str.toUpperCase())}

                        </label>
                      ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="addAccount-saveButtonContainer">
              <Button
                type="submit"
                className="addAccount-saveButton"
                text={isSubmitting ? "Saving..." : "Save"}
                color="#006d7d"
                disabled={!isFormValid() || isSubmitting}
                onClick={() => {
                  const form = document.querySelector("form");
                  if (form && !form.checkValidity()) {
                    form.reportValidity();
                    const isUpdate = !!editMode;
                    setPopupMessage(
                      isUpdate
                        ? "Error updating account!"
                        : "Error saving account!"
                    );
                    setPopupType("error");
                    setShowPopup(true);
                    setTimeout(() => setShowPopup(false), 4000);
                  }
                }}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddAccount;